import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCookies } from "@/lib/cookies";
import { useI18n } from "@/lib/i18n";

export default function CookieBanner() {
  const { prefs, save, hasChosen } = useCookies();
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const [localPrefs, setLocalPrefs] = useState({
    analytics: prefs.analytics ?? false,
    marketing: prefs.marketing ?? false,
  });

  const showBanner = !hasChosen || prefs._showBanner;
  if (!showBanner) return null;

  const acceptAll = () => save({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => save({ necessary: true, analytics: false, marketing: false });
  const savePrefs = () => save({ necessary: true, ...localPrefs });

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-labelledby="cookie-banner-title"
      className="fixed inset-x-0 bottom-0 z-[100] bg-white border-t border-soft shadow-[0_-20px_40px_-20px_rgba(26,54,54,0.15)]"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-6">
        {!expanded ? (
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
            <div className="max-w-2xl">
              <h3 id="cookie-banner-title" className="font-display text-xl text-brand mb-1">
                {t("cookie.title")}
              </h3>
              <p className="text-sm text-muted-ink leading-relaxed">
                {t("cookie.text")}{" "}
                <Link to="/cookie" className="link-underline text-brand">
                  Cookie Policy
                </Link>
              </p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <button
                onClick={() => setExpanded(true)}
                className="px-4 py-2 text-xs uppercase tracking-editorial border border-soft hover:bg-cream-warm transition-colors"
                data-testid="cookie-customize"
              >
                {t("cookie.customize")}
              </button>
              <button
                onClick={rejectAll}
                className="px-4 py-2 text-xs uppercase tracking-editorial border border-soft hover:bg-cream-warm transition-colors"
                data-testid="cookie-reject"
              >
                {t("cookie.reject")}
              </button>
              <button
                onClick={acceptAll}
                className="px-4 py-2 text-xs uppercase tracking-editorial bg-brand text-white hover:bg-[#0f2020] transition-colors"
                data-testid="cookie-accept"
              >
                {t("cookie.accept")}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <h3 className="font-display text-xl text-brand mb-4">{t("cookie.title")}</h3>
            <div className="grid gap-3 md:grid-cols-3 mb-5">
              <div className="border border-soft p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">{t("cookie.necessary")}</span>
                  <span className="text-xs text-muted-ink">Sempre attivi</span>
                </div>
                <p className="text-xs text-muted-ink">Necessari al funzionamento del sito.</p>
              </div>
              <label className="border border-soft p-4 cursor-pointer">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">{t("cookie.analytics")}</span>
                  <input
                    type="checkbox"
                    checked={localPrefs.analytics}
                    onChange={(e) => setLocalPrefs((p) => ({ ...p, analytics: e.target.checked }))}
                    data-testid="cookie-analytics-toggle"
                    className="h-4 w-4"
                  />
                </div>
                <p className="text-xs text-muted-ink">Aiutano a comprendere l'utilizzo del sito in modo anonimo.</p>
              </label>
              <label className="border border-soft p-4 cursor-pointer">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">{t("cookie.marketing")}</span>
                  <input
                    type="checkbox"
                    checked={localPrefs.marketing}
                    onChange={(e) => setLocalPrefs((p) => ({ ...p, marketing: e.target.checked }))}
                    data-testid="cookie-marketing-toggle"
                    className="h-4 w-4"
                  />
                </div>
                <p className="text-xs text-muted-ink">Utilizzati per comunicazioni promozionali personalizzate.</p>
              </label>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                onClick={rejectAll}
                className="px-4 py-2 text-xs uppercase tracking-editorial border border-soft hover:bg-cream-warm transition-colors"
              >
                {t("cookie.reject")}
              </button>
              <button
                onClick={savePrefs}
                className="px-4 py-2 text-xs uppercase tracking-editorial border border-soft hover:bg-cream-warm transition-colors"
                data-testid="cookie-save"
              >
                {t("cookie.save")}
              </button>
              <button
                onClick={acceptAll}
                className="px-4 py-2 text-xs uppercase tracking-editorial bg-brand text-white hover:bg-[#0f2020] transition-colors"
              >
                {t("cookie.accept")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
